import { useId } from 'react';

interface LogoProps {
  /** 'color' = green icon + navy wordmark (light surfaces). 'white' = all white (dark surfaces). */
  variant?: 'color' | 'white';
  /** Height in px. Width scales automatically at native 3.29:1 ratio (293:89). */
  height?: number;
  className?: string;
  'aria-hidden'?: boolean | 'true' | 'false';
}

/**
 * Official NEXTRAISE vector logo, exported from Figma.
 * Use variant="color" in the public header; variant="white" in the admin nav and footer.
 * Always render inside an anchor/link that carries the accessible name — pass aria-hidden="true".
 */
export function Logo({
  variant = 'color',
  height = 36,
  className,
  'aria-hidden': ariaHidden = true,
}: LogoProps) {
  const uid = useId().replace(/:/g, '');
  const green = variant === 'color' ? '#00834F' : '#ffffff';
  const dark = variant === 'color' ? '#081B28' : '#ffffff';

  return (
    <svg
      height={height}
      viewBox="277.5 35.5 293 89"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ width: 'auto', display: 'block' }}
      aria-hidden={ariaHidden}
    >
      <defs>
        <clipPath id={`clip-${uid}`}>
          <rect width="293" height="89" fill="white" transform="translate(277.5 35.5)" />
        </clipPath>
        <mask
          id={`mask0-${uid}`}
          style={{ maskType: 'luminance' }}
          maskUnits="userSpaceOnUse"
          x="277"
          y="35"
          width="294"
          height="90"
        >
          <path d="M570.5 35.5H277.5V124.5H570.5V35.5Z" fill="white" />
        </mask>
        <mask
          id={`mask1-${uid}`}
          style={{ maskType: 'luminance' }}
          maskUnits="userSpaceOnUse"
          x="277"
          y="35"
          width="294"
          height="90"
        >
          <path d="M570.5 35.5H277.5V124.5H570.5V35.5Z" fill="white" />
        </mask>
      </defs>

      <g clipPath={`url(#clip-${uid})`}>
        {/* I — vertical stem of RAISE */}
        <path
          d="M463.45 91.1396L463.452 123.934H458.209L458.208 91.1396H463.45Z"
          fill={green}
        />

        {/* Trademark superscript + M-shaped icon */}
        <g mask={`url(#mask0-${uid})`}>
          <path
            d="M569.546 37.5864L569.235 37.4868L567.696 41.0915L566.501 38.4445C566.377 38.1656 566.296 37.9436 565.966 37.6832L565.775 40.9677L564.872 40.9549L564.849 35.6367L566.251 35.6794L567.702 39.1148L569.151 35.6509H570.5L570.494 40.9535L569.54 40.9691L569.546 37.5892V37.5864Z"
            fill={dark}
          />
          <path
            d="M563.302 40.6872C562.212 41.4771 560.807 41.1654 559.776 40.35L560.28 39.6114C560.986 40.2276 561.959 40.3443 562.745 40.0568C562.919 39.9074 562.958 39.1987 562.747 39.1261L560.819 38.5227C560.427 38.4003 560.008 38.0645 559.905 37.7528C559.759 37.3102 559.826 36.8078 560.124 36.3994C560.917 35.3122 562.522 35.3748 563.738 36.2756C563.614 36.5033 563.346 36.7979 563.128 36.9658C562.539 36.5474 561.649 36.4421 561.097 36.6328C560.931 36.6897 560.784 37.1252 560.855 37.2632C561.186 37.9193 562.228 37.6788 563.26 38.3092C563.628 38.5327 563.97 39.0948 563.966 39.4264C563.96 39.822 563.711 40.3898 563.302 40.6872Z"
            fill={dark}
          />
          {/* M-shaped icon */}
          <path
            d="M294.85 56.0655C295.836 57.2737 296.146 58.6755 296.697 59.9833L297.921 62.8907L307.368 85.6349C307.781 86.012 308.636 85.975 308.997 85.6264L311.816 78.6318L312.267 77.5161L319.768 59.1593C320.26 57.9554 320.698 56.9322 321.319 55.6969L321.322 85.8356H326.701L326.708 59.7457L323.441 54.9427L318.969 48.3266C318.706 48.338 318.355 48.5486 318.274 48.7535L316.024 54.4503L310.673 67.7549L308.091 74.3411L306.532 70.3863L303.134 61.9799L297.563 48.3864L289.137 48.3793L289.14 123.884L277.486 123.904L277.489 35.5969L355.993 35.5869L355.997 38.9085L353.192 43.0753L350.226 47.6592L339.072 64.3765L333.311 55.8478L328.242 48.3608L322.471 48.3636L323.359 49.7753L332.217 62.6104L333.256 64.1374L336.428 68.7696L336.422 85.8626L341.496 85.8455V68.7653L348.736 58.3866L355.999 47.9979L355.992 123.898L294.864 123.89L294.85 56.0655Z"
            fill={green}
          />
        </g>

        {/* R — of RAISE */}
        <path
          d="M383.469 104.853C385.166 102.709 385.117 99.7735 383.475 97.6659C382.3 96.5444 380.828 95.776 379.146 95.7674L370.277 95.7247L370.287 106.752L379.431 106.744C380.968 106.425 382.331 106.022 383.468 104.853H383.469ZM382.294 111.173L383.114 112.379L390.487 123.915L384.113 123.928L376.302 111.22L370.279 111.238L370.284 123.932L365.095 123.917L365.09 91.0057L378.067 91C379.596 91 381.03 91.0441 382.48 91.3785C386.03 92.1257 389.07 94.7499 389.887 98.2892C390.811 102.295 389.925 106.947 386.466 109.12C385.326 109.836 384.003 110.424 382.727 110.626L382.249 110.622C382.129 110.622 382.223 111.072 382.293 111.173H382.294Z"
          fill={green}
        />

        {/* A — of RAISE */}
        <path
          d="M425.649 112.657L424.341 108.958L419.255 95.9209L412.676 112.668L425.649 112.657ZM435.866 123.922L429.992 123.929L427.328 117.307L411.041 117.295L408.486 123.914L402.612 123.918L403.87 120.743L415.841 91.172L422.572 91.1421L427.488 103.18L429.007 106.946L433.821 118.796L434.339 120.085L435.866 123.921V123.922Z"
          fill={green}
        />

        {/* S + E — of RAISE */}
        <g mask={`url(#mask1-${uid})`}>
          <path
            d="M555.225 104.884L555.162 109.404L538.163 109.412L538.15 119.314L555.636 119.319L555.691 123.939L532.861 123.929L532.868 91.0371L538.371 91.0755L555.671 91.1581L555.697 95.0076C555.698 95.2467 555.466 95.6836 555.31 95.8401L538.177 95.833L538.158 104.875L555.225 104.884Z"
            fill={green}
          />
          <path
            d="M508.85 123.599C504.408 124.948 499.758 124.736 495.427 123.258C493.019 122.435 491.097 120.961 489.25 119.222L492.447 115.103C494.442 117.42 496.97 118.81 499.826 119.455C503.095 120.286 507.91 119.879 509.582 117.015C510.854 114.833 509.89 112.273 507.655 111.304L502.78 109.693L501.577 109.38C499.237 108.77 496.885 108.248 494.767 107.14C492.249 105.822 490.669 103.495 490.44 100.644C490.131 96.7892 492.395 93.3951 495.868 91.861C502.006 89.15 509.859 90.4749 514.89 95.073L511.752 99.247C508.754 95.6991 501.748 94.0583 497.861 96.2883C496.125 97.2845 495.348 99.5188 496.544 101.16C497.321 102.227 498.633 102.873 499.897 103.225L504.694 104.558C509.376 105.86 514.161 107.218 515.278 112.33C516.351 117.244 513.806 122.092 508.85 123.598V123.599Z"
            fill={green}
          />
        </g>

        {/* N — of NEXT */}
        <path
          d="M367.041 35.6452L379.814 35.5L400.896 70.1442H401.037V35.5H411.963V85.3545H397.675L376.032 49.8649L367.041 35.6452Z"
          fill={dark}
        />
        {/* E — of NEXT */}
        <path
          d="M421.766 35.5H455.456V45.6397H432.692V54.9354H454.195V65.0751H432.692V75.2148H456.716V85.3545H421.766V35.5Z"
          fill={dark}
        />
        {/* X — of NEXT */}
        <path
          d="M479.899 59.3001L463.509 35.5H477.236L487.813 52.963L497.968 35.5H511.205L495.166 59.0895L513.098 85.3545H499.369L487.252 65.2857L475.485 85.3545H462.528L479.899 59.3001Z"
          fill={dark}
        />
        {/* T — of NEXT */}
        <path
          d="M531.236 45.217H517.089V35.5H556.31V45.217H542.162V85.3545H531.236V45.217Z"
          fill={dark}
        />
        {/* N extension */}
        <path
          d="M375.892 58.4717V85.3543L365.066 85.3514L365.079 42.5371C366.289 43.872 366.925 45.1286 367.893 46.3824L375.893 58.4703L375.892 58.4717Z"
          fill={dark}
        />
      </g>
    </svg>
  );
}
